// deployment_class: customer_env_allowed
/**
 * CEO redesign — the full project view (Increment 4). Opens from a project card.
 * Leads with the lifecycle (pulsing "now") and the DATA TICKER: a band of live
 * data tiles that slowly auto-scrolls, PAUSES on hover, is GRAB-scrollable so the
 * CEO can scrub to the data without waiting, and whose tiles HIGHLIGHT on hover
 * and open a "what it means" drill on click (per E3 2c/2d). Then sources & uses,
 * partner + next steps, and provenance. Prev/Next walks the pipeline in place.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { PHASES } from './project-data';
import { useDeals } from './dashboard-source';
import { Cover } from './project-visual';
import { tickExplain } from './Drill';
import './project.css';

function Donut({ v }: { v: number }): JSX.Element {
  const r = 26, c = 2 * Math.PI * r, off = c * (1 - v / 100);
  const col = v >= 65 ? '#2f7d52' : v >= 50 ? '#b07a16' : '#bf4631';
  return (
    <svg width="74" height="74" viewBox="0 0 74 74" role="img" aria-label={`${v}%`}>
      <circle cx="37" cy="37" r={r} fill="none" stroke="#f0ede6" strokeWidth="9" />
      <circle cx="37" cy="37" r={r} fill="none" stroke={col} strokeWidth="9" strokeLinecap="round" strokeDasharray={c.toFixed(1)} strokeDashoffset={off.toFixed(1)} transform="rotate(-90 37 37)" />
      <text x="37" y="42" textAnchor="middle" style={{ fontWeight: 700, fontSize: 16, fill: '#16181d' }}>{v}%</text>
    </svg>
  );
}

interface Facet { label: string; donut?: number; value?: string; }

const REDUCED_MOTION = !!(typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

function DataTicker({ facets, onTick }: { facets: Facet[]; onTick: (m: string, v?: string) => void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const paused = useRef(false); // transient hover/drag pause
  const drag = useRef({ down: false, sx: 0, sl: 0, moved: 0 });
  const [hovered, setHovered] = useState<Facet | null>(null);
  // WCAG 2.2.2 (Pause/Stop/Hide) — auto-scroll is user-controllable and OFF by
  // default under prefers-reduced-motion (Fable #7). Grab-to-scroll always works.
  const [playing, setPlaying] = useState(!REDUCED_MOTION);

  useEffect(() => {
    const el = ref.current;
    if (!el || !playing) return undefined;
    const id = window.setInterval(() => {
      if (paused.current) return;
      el.scrollLeft += 0.5;
      if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
    }, 28);
    return () => window.clearInterval(id);
  }, [playing]);

  const onDown = (e: PointerEvent<HTMLDivElement>): void => {
    const el = ref.current; if (!el) return;
    // NOTE: do NOT capture the pointer here — capturing on every press routed the
    // subsequent `click` to the scroller, so a plain tap on a tile never reached
    // the tile's onClick ("no opportunity to open in its own view"). We capture
    // only once a real drag starts (onMove), so pure clicks open the tile drill.
    drag.current = { down: true, sx: e.clientX, sl: el.scrollLeft, moved: 0 };
    paused.current = true; el.classList.add('grabbing');
  };
  const onMove = (e: PointerEvent<HTMLDivElement>): void => {
    const el = ref.current; if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.sx;
    drag.current.moved = Math.max(drag.current.moved, Math.abs(dx));
    if (drag.current.moved > 5) el.setPointerCapture?.(e.pointerId); // capture only while dragging
    el.scrollLeft = drag.current.sl - dx;
  };
  const onUp = (): void => {
    drag.current.down = false; ref.current?.classList.remove('grabbing');
    window.setTimeout(() => { paused.current = false; }, 900);
  };
  const tap = (f: Facet): void => { if (drag.current.moved <= 5) onTick(f.label, f.value); };

  // Real tiles (tabbable, in the a11y tree) once; the loop clones are aria-hidden
  // + not focusable so keyboard users don't tab through every metric twice (Fable #7).
  const renderTile = (f: Facet, i: number, clone: boolean): JSX.Element => (
    <div className="kd-scard" key={clone ? `c${i}` : i}
      role={clone ? undefined : 'button'} tabIndex={clone ? -1 : 0} aria-hidden={clone || undefined}
      onClick={() => tap(f)}
      onMouseEnter={() => setHovered(f)}
      onFocus={clone ? undefined : () => setHovered(f)}
      onKeyDown={clone ? undefined : (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTick(f.label, f.value); } }}
      aria-label={clone ? undefined : `${f.label}: ${f.value ?? `${f.donut}%`}`}>
      {f.donut != null ? <Donut v={f.donut} /> : null}
      <div className="kd-st">{f.label}</div>
      {f.value != null ? <div className="kd-sv">{f.value}</div> : null}
    </div>
  );

  return (
    <div className="kd-tickwrap">
      {hovered && (
        <div className="kd-tickprev" role="note">
          <div className="kd-tickprevh">{hovered.label}{hovered.value != null ? ` · ${hovered.value}` : hovered.donut != null ? ` · ${hovered.donut}%` : ''}</div>
          <div className="kd-tickprevb">{tickExplain(hovered.label)}</div>
          <div className="kd-tickprevo" aria-hidden="true">Click the tile to open in its own view →</div>
        </div>
      )}
      <button className="kd-tickplay" onClick={() => setPlaying((p) => !p)}
        aria-label={playing ? 'Pause the data ticker' : 'Play the data ticker'}>{playing ? '⏸' : '▶'}</button>
      <div className="kd-ticker" ref={ref} role="group" aria-label="Data Ticker — grab to scroll"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
        onMouseEnter={() => { paused.current = true; }}
        onMouseLeave={() => { if (!drag.current.down) paused.current = false; setHovered(null); }}>
        <div className="kd-tickrow">
          {facets.map((f, i) => renderTile(f, i, false))}
          {facets.map((f, i) => renderTile(f, i, true))}
        </div>
      </div>
    </div>
  );
}

export function ProjectView({ dealKey, onBack, onOpen, onTick, onMilestone, pinned, onTogglePin, isCardPinned, onToggleCardPin }: {
  dealKey: string; onBack: () => void; onOpen: (k: string) => void;
  onTick: (m: string, v?: string) => void; onMilestone: (idx: number) => void;
  pinned: boolean; onTogglePin: (label: string) => void;
  isCardPinned: (id: string) => boolean; onToggleCardPin: (id: string, label: string) => void;
}): JSX.Element {
  const { deals: DEALS, order: DEAL_ORDER } = useDeals();
  // A pin on a project-view card — clicking its rail shortcut reopens this project
  // (where the card lives). Same .kd-pin styling as everywhere else.
  const cardPin = (id: string, label: string): JSX.Element => (
    <button
      className={`kd-pin kd-pin-card ${isCardPinned(id) ? 'on' : ''}`}
      aria-label={`${isCardPinned(id) ? 'Unpin' : 'Pin'} ${label}`}
      aria-pressed={isCardPinned(id)}
      title={`${isCardPinned(id) ? 'Unpin' : 'Pin'} ${label}`}
      onClick={(e) => { e.stopPropagation(); onToggleCardPin(id, label); }}
    >📌</button>
  );
  const d = DEALS[dealKey];
  const idx = DEAL_ORDER.indexOf(dealKey);
  const prev = DEAL_ORDER[(idx - 1 + DEAL_ORDER.length) % DEAL_ORDER.length];
  const next = DEAL_ORDER[(idx + 1) % DEAL_ORDER.length];
  const facets: Facet[] = [
    { label: 'Progress', donut: d.progress },
    { label: 'Phase', value: `${d.phaseIdx + 1} / 9` },
    { label: 'Budget', value: d.budget },
    { label: 'DSCR', value: d.dscr },
    { label: 'Status', value: d.status },
    ...d.su.map((s): Facet => ({ label: s[0], value: s[2] })),
    { label: 'Partner', value: d.partner.split(' · ')[0] },
  ];

  return (
    <>
      <div className="kd-pvhead">
        <button className="kd-btn" onClick={onBack}>← Back to pipeline</button>
        <div className="kd-pvnav">
          <button
            className={`kd-pin kd-pin-pv ${pinned ? 'on' : ''}`}
            aria-label={`${pinned ? 'Unpin' : 'Pin'} ${d.name}`}
            aria-pressed={pinned}
            title={`${pinned ? 'Unpin' : 'Pin'} ${d.name}`}
            onClick={() => onTogglePin(d.name)}
          >📌</button>
          <button className="kd-btn" onClick={() => onOpen(prev)} aria-label="Previous project">‹ Prev</button>
          <button className="kd-btn" onClick={() => onOpen(next)} aria-label="Next project">Next ›</button>
        </div>
      </div>
      {/* HERO — the picture view CARRIES the Data Ticker (E3 2026-06-30) */}
      <div className="kd-pvhero">
        <Cover d={d} big />
        <div className="kd-pvhero-in">
          <div className="kd-eyebrow light">Project · {d.type}</div>
          <h1 className="kd-h1 light">{d.name}</h1>
          <div className="kd-pvhero-meta">{d.dev} · {d.budget} TDC · {d.units} units · phase {d.phaseIdx + 1}/9</div>
        </div>
        <div className="kd-pvticker">
          <DataTicker facets={facets} onTick={onTick} />
          <span className="kd-tickhint">grab to scroll · tap a tile for what it means</span>
        </div>
      </div>

      <div className="kd-sec"><h2>Lifecycle</h2><span className="hint">click a milestone for its workflow &amp; data</span></div>
      <div className="kd-pvtrack"><div className="kd-dstepper kd-life" role="list" aria-label="Project lifecycle milestones">
        {PHASES.map((p, i) => (
          <div className={`kd-step kd-mile ${i < d.phaseIdx ? 'done' : ''} ${i === d.phaseIdx ? 'now' : ''}`} key={i}
            role="button" tabIndex={0}
            aria-label={`${p} — milestone ${i + 1} of ${PHASES.length}${i === d.phaseIdx ? ' · current' : i < d.phaseIdx ? ' · completed' : ' · upcoming'}`}
            onClick={() => onMilestone(i)}
            onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onMilestone(i); } }}>
            <div className="kd-sg" /><div className="kd-sd" /><div className="kd-sl">{p}</div>
          </div>
        ))}
      </div></div>

      <div className="kd-sec"><h2>Sources &amp; uses</h2></div>
      <div className="kd-grid">
        <div className="kd-card">
          <div className="kd-ch"><div className="kd-ci">$</div><div className="kd-cn">Capital structure</div>{cardPin(`project:${dealKey}:capital`, `${d.name} — Capital structure`)}</div>
          <div className="kd-bars">{d.su.map((s, i) => (
            <div className="kd-bar" key={i}><span className="bn">{s[0]}</span><span className="bt"><span className="bf" style={{ width: `${s[1]}%` }} /></span><span className="bv">{s[2]}</span></div>
          ))}</div>
        </div>
        <div className="kd-card">
          <div className="kd-ch"><div className="kd-ci">◆</div><div className="kd-cn">Partner &amp; next steps</div>{cardPin(`project:${dealKey}:partner`, `${d.name} — Partner & next steps`)}</div>
          <div className="kd-meta">{d.partner}</div>
          <div className="kd-dsec" style={{ marginTop: 12 }}>Next actions</div>
          {d.next.map((n, i) => (<div className="kd-pvnext" key={i}><span>{n}</span><span style={{ color: 'var(--kd-teal-d)' }}>→</span></div>))}
        </div>
      </div>

      <div className="kd-sec"><h2>Provenance</h2></div>
      <div style={{ margin: '0 30px' }}><div className="kd-prov">✓ Every figure resolves to the signed JV agreement · <b>Merkle-anchored</b> · one-click-auditable. “Verify me,” not “trust me.”</div></div>
    </>
  );
}
